/**
 * Catalogue detail panel — per-objective detail view + edit form.
 *
 * `renderCatalogueDetail` paints the selected objective (read mode or
 * edit mode), `getObjectiveDetail` reads the data from `_observationDefinition`
 * + `_activityDefinition` for display, `saveEditingObjective` writes the
 * planner's edits back to Firestore (includes the OMON-059 local-cache
 * mirror that fixes the silent-save-on-attainment bug).
 *
 * Reaches back into main for `showDeleteModal` (still in main, moves in
 * 6c.4) and `statusEl` (would be in dom, but check).
 *
 * Extracted from main.ts as part of OMON-060 Phase 6c.2.
 */

import { doc, getDoc, updateDoc, setDoc, deleteField } from "firebase/firestore";

import { state } from "../state";
import { db, firestoreDatabaseId } from "../firebase";
import { recordingKindChoices } from "./recording-kinds";
import { catalogueList, statusEl } from "../dom";
import { escapeHtml } from "../utils/html";
import {
  ALL_SHIFT_ROLE_CODES, SHIFT_ROLES, defaultsOf, rolesLocked, type ShiftRoleCode,
} from "../shift-roles";
import { renderPromoteSection } from "./promote";
import { renderProvenanceSection } from "./provenance-section";
import { resolveODHierarchy } from "./resolvers";
import { buildCategoryArray, setCapabilityCodings, resolveCategoryCodes } from "./category";
import { trackableCapabilities } from "../types";
import type { ObservationDefinition, ActivityDefinition } from "../types";
import { metaSecurityFor } from "../admin/meta-security";
import { renderCatalogue } from "./tree";
import { LEVEL_NAMES } from "../main";
import {
  parse as parseGuidance,
  stripGuidanceHeader,
  applyGuidanceHeader,
} from "./guidanceParser";
import { editPreviewToggleHtml, guidancePreviewHtml } from "./editPreviewToggle";
import { guidanceEditorHtml, wireGuidanceEditor } from "../guidance/editor";
import { eventGuidanceText, guidanceTextChanged, resolveGuidanceText } from "../guidance/single-text";
import { changedMonitoredTextFields, type MonitoredTextWrites } from "./monitoredText";
import { diffEditingDetail, showToast, showUndoToast } from "./saveModal";
import { autosizeTextareas } from "../utils/autosize";
import { partsOf, withPartPrompt } from "../clients/event-parts";

// Catalogue Detail Panel
// ======================================================================

/**
 * OMON-232 (Ed, 2026-10-01): save one part's default prompt. Writes the
 * fact's `component[]` with ONLY that part's `_prompt` changed (codes,
 * labels and data types untouched — `withPartPrompt`), outside the main
 * edit diff so nothing else on the fact can ride along. No-op when unchanged.
 */
async function partPromptSave(name: string, code: string, value: string): Promise<void> {
  const od = state.catalogueData.get(name);
  const part = partsOf(od).find((p) => p.code === code);
  if (!od || !part || part.defaultPrompt === value.trim()) return;
  const next = withPartPrompt(od.component, code, value);
  try {
    await updateDoc(doc(db, "_observationDefinition", name), { component: next });
    od.component = next;
    showToast(`Saved the prompt for "${part.label}"`);
  } catch (err) {
    showToast(`Couldn't save the prompt for "${part.label}": ${(err as Error).message}`, "error");
  }
}

/**
 * OMON-068 follow-up: read all live edit-mode form values into
 * `state.editingDetail`. Called before any operation that re-renders
 * the panel mid-edit (category cascade, preview toggle) so the user's
 * typing isn't lost. Idempotent — safe to call multiple times.
 */
function captureEditingDetailFromForm() {
  if (!state.editingDetail) return;
  const titleEl = catalogueList.querySelector("#editTitle") as HTMLInputElement | null;
  const smartEl = catalogueList.querySelector("#editSmartGoal") as HTMLTextAreaElement | null;
  if (titleEl) state.editingDetail.title = titleEl.value;
  if (smartEl) state.editingDetail.smartGoal = smartEl.value;
  // OMON-174: monitored-event prompt textarea (only present when M is on).
  const mepEl = catalogueList.querySelector("#editMonitoredEventPrompt") as HTMLTextAreaElement | null;
  if (mepEl) state.editingDetail.monitoredEventPrompt = mepEl.value;
  // Zammad #49088: monitored-event carer guidance (only present on its tab).
  const megEl = catalogueList.querySelector("#editMonitoredEventGuidance") as HTMLTextAreaElement | null;
  if (megEl) state.editingDetail.monitoredEventGuidance = megEl.value;
  // Ed 2026-09-29: a goal's one carer-guidance text (only present on its tab).
  const gtEl = catalogueList.querySelector("#editCarerGuidanceText") as HTMLTextAreaElement | null;
  if (gtEl) state.editingDetail.guidanceText = gtEl.value;
  const kindEl = catalogueList.querySelector("#editRecordingKind") as HTMLSelectElement | null;
  if (kindEl) state.editingDetail.recordingKind = kindEl.value;
  for (let i = 0; i < 5; i++) {
    const attEl = catalogueList.querySelector(`[data-edit-field="att-${i}"]`) as HTMLTextAreaElement | null;
    const prEl = catalogueList.querySelector(`[data-edit-field="pr-${i}"]`) as HTMLTextAreaElement | null;
    if (attEl) state.editingDetail.attainment[i] = attEl.value;
    // OMON-098: textarea only contains the editable tail (header stripped
    // on display). Reconstruct the stored shape with current title +
    // subcategory display before writing to state.
    if (prEl) state.editingDetail.prompts[i] = wrapPromptWithHeader(prEl.value);
  }
}

/**
 * OMON-098: rebuild the stored prompt shape from the textarea's
 * editable tail + the current goal title and subcategory display.
 * Looks up the subcategory display from state.goalSubcategories using
 * the composite `<categoryCode>__<subcategoryCode>` key.
 */
function wrapPromptWithHeader(editableTail: string): string {
  if (!state.editingDetail) return editableTail;
  const subKey = `${state.editingDetail.categoryCode}__${state.editingDetail.subcategoryCode}`;
  const subcatNode = state.goalSubcategories.get(subKey);
  const subtitle = subcatNode?.display || state.editingDetail.subcategoryCode || "";
  return applyGuidanceHeader(editableTail, state.editingDetail.title, subtitle);
}

/**
 * OMON-172: read the librarian per-fact policy flags off an ObservationDefinition
 * into the EditingDetail/OriginalSnapshot shape. One reader for all three seed
 * sites (inspector seed, classic-edit seed, diff snapshot) so they can't drift.
 */
function policyFlagsFromOd(od: ObservationDefinition): {
  targetable: boolean; escalatable: boolean; escalationThreshold: number | null; mandatory: boolean;
  shiftRolesDefault: ShiftRoleCode[]; rolesLocked: boolean;
} {
  // ADR-0035 am.8: the role default (legacy `_mandatory` reads as every role).
  const roles = defaultsOf(od as unknown as Record<string, unknown>);
  return {
    targetable: od._targetable === true,
    escalatable: od._escalatable === true,
    escalationThreshold: typeof od._escalationThresholdDefault === "number" ? od._escalationThresholdDefault : null,
    mandatory: roles.length > 0,
    shiftRolesDefault: roles,
    rolesLocked: rolesLocked(od as unknown as Record<string, unknown>),
  };
}

export function getObjectiveDetail(name: string): {
  smartGoal: string; monitoredEventPrompt: string; monitoredEventGuidance: string; guidanceText: string;
  attainment: string[]; prompts: string[];
} {
  const od = state.catalogueData.get(name);
  const ad = state.activityData.get(name);
  const title = od?.title?.toLowerCase() || name;

  // SMART goals are a SOMA-developmental-OD concept. Monitored events
  // (three-state-ordinal recording obligations) aren't goals, so they
  // don't have a SMART goal template. Default-soma convention per
  // OMON-117: absent `_measurementType` ⇒ soma. Anything else ⇒ not a goal.
  // OMON-166: SMART goal is the content of the Developmental capability. Show it
  // when the fact carries the `developmental` trackable-capability coding
  // (replaces the legacy `_measurementType` heuristic).
  const hasDevelopmental = od ? trackableCapabilities(od).includes("developmental") : false;
  const smartGoal = hasDevelopmental
    ? (od?._smartGoalTemplate || `Over the next 8 weeks, the person will ${title} in at least 4 out of 5 opportunities with the least intrusive level of support.`)
    : "";

  // OMON-174: the monitored-event prompt is the Monitored capability's content,
  // exactly as SMART goal is the Developmental capability's. No boilerplate
  // default — empty until the librarian authors one (the SMART generated
  // sentence is a SOMA-ism that doesn't transfer).
  const hasMonitored = od ? trackableCapabilities(od).includes("monitored") : false;
  const monitoredEventPrompt = hasMonitored ? (od?._monitoredEventPrompt || "") : "";
  // Zammad #49088: the event's carer guidance, same rule — no default text.
  // Ed 2026-09-29: read in the one-text rules (legacy `##` sections → folds).
  const monitoredEventGuidance = hasMonitored ? eventGuidanceText(od?._monitoredEventGuidance) : "";

  // Extract attainment descriptions from qualifiedValue
  const attainment: string[] = [];
  if (od?.qualifiedValue) {
    for (const qv of od.qualifiedValue) {
      attainment.push(qv.context?.description || qv.context?.text || "");
    }
  }
  while (attainment.length < 5) {
    const level = attainment.length + 1;
    const labels = [
      `Needs full physical assistance and continuous direction to ${title}.`,
      `Completes parts of the task but still needs frequent physical assistance or modelling to ${title}.`,
      `Can ${title} with verbal or visual prompts but no physical assistance.`,
      `Can ${title} with only occasional check-ins, reassurance or a single reminder.`,
      `Consistently ${title} safely and appropriately with no staff support.`,
    ];
    attainment.push(labels[level - 1] || "");
  }

  // Extract prompts from activity documentation
  const prompts: string[] = [];
  if (ad?.documentation) {
    for (const doc of ad.documentation) {
      prompts.push(doc.display || doc.label || "");
    }
  }
  while (prompts.length < 5) {
    const level = prompts.length + 1;
    const defaults = [
      "Full hands-on assistance. Step-by-step directions throughout.",
      "Client participates; staff assists with harder parts.",
      "Verbal or visual prompt only. No physical help.",
      "Brief check-in. Single reminder if needed.",
      "Completes independently. Staff acknowledges success.",
    ];
    prompts.push(defaults[level - 1] || "");
  }

  // Ed 2026-09-29: the goal's carer guidance as ONE text — the saved text, else
  // the legacy 5 levels composed (never the placeholder defaults above).
  const guidanceText = resolveGuidanceText(null, ad as unknown as Record<string, unknown>) ?? "";

  return {
    smartGoal, monitoredEventPrompt, monitoredEventGuidance, guidanceText,
    attainment: attainment.slice(0, 5), prompts: prompts.slice(0, 5),
  };
}

// OMON-166 Part 2.5: which fact the always-edit working copy currently represents,
// so the inspector re-inits on a new selection but not on same-fact re-renders.
let editingForName: string | null = null;

// OMON-232: monotonic edit-session counter, bumped on every fact switch (always via
// beginEditSession). A save captures it on entry and aborts if it moves before the
// write lands — the durable cure for the recurring catalogue cross-fact overwrite.
let editSessionEpoch = 0;

/**
 * OMON-232: the ONLY mutator of "which fact is being edited". Bumps the epoch
 * (invalidating any in-flight save), clears the working copy so the next render
 * reseeds for `name`, and points the selection at it. Every place that switches
 * facts must route through here rather than setting the three state fields by hand.
 */
export function beginEditSession(name: string | null): void {
  editSessionEpoch++;
  state.catalogueSelectedGoal = name;
  editingForName = null;
  state.editingDetail = null;
}

// 2026-06-10 (Ed): the capability pills are a single-select VIEW switch — one
// capability's content (SMART goal for D, monitored-event prompt for M) shows
// at a time so a multi-capability fact doesn't stack every section. The fact's
// GRANTED set is unchanged data (ADR-0019 affordance menu); this is only which
// lens the editor is looking through. Session-scoped, reseeded per fact.
let capabilityView: string | null = null;

// 2026-06-10 (Ed): per-level Edit | Preview toggle for the Carer Guidance
// Prompts tab in the always-edit inspector — same write/preview-tabs concept
// as the plan-builder's OMON-135 guidance editor (the classic drill-in's
// global Preview button never rendered inside the inspector pane). Levels in
// this set are being EDITED; every other level shows the rendered preview
// (Ed, 2026-09-24: preview is the default — you read guidance far more often
// than you write it). Session-scoped, reset per fact.
// Zammad #49088: the same set carries "event" for a monitored event's single
// guidance text, so both use one toggle (keys: "0".."4" | "event").
let guidanceEditKeys = new Set<string>();
const CAP_VIEW_ORDER = ["developmental", "monitored", "safety"] as const;
function firstGrantedCapability(caps: string[]): string | null {
  return CAP_VIEW_ORDER.find((c) => caps.includes(c)) || null;
}

export function renderCatalogueDetail() {
  const name = state.catalogueSelectedGoal!;
  const od = state.catalogueData.get(name);
  if (!od) { beginEditSession(null); renderCatalogue(); return; }

  // OMON-166 Part 2.5: inside the 50/50 inspector pane the editor is ALWAYS-EDIT
  // (no read view, no Edit button) and auto-saves. Seed the working copy when a new
  // fact is selected; preserve it across re-renders of the same fact (pill toggles etc.).
  const inInspectorPane = !!catalogueList.querySelector("#catInspectorPane");
  if (inInspectorPane && (!state.editingDetail || editingForName !== name)) {
    const seed = getObjectiveDetail(name);
    const sh = resolveODHierarchy(od);
    state.editingDetail = {
      title: od.title, smartGoal: seed.smartGoal, monitoredEventPrompt: seed.monitoredEventPrompt,
      monitoredEventGuidance: seed.monitoredEventGuidance, guidanceText: seed.guidanceText,
      attainment: [...seed.attainment], prompts: [...seed.prompts],
      categoryCode: sh.categoryCode, subcategoryCode: sh.subcategoryCode,
      capabilities: trackableCapabilities(od), recordingKind: od._recordingKind || "attainment-5",
      status: od.status === "retired" ? "retired" : "active",
      ...policyFlagsFromOd(od),
    };
    state.catalogueEditMode = true;
    editingForName = name;
    capabilityView = firstGrantedCapability(state.editingDetail.capabilities);
    guidanceEditKeys = new Set<string>();
  }
  // Keep the view pointing at a granted capability (covers revoke + external
  // changes); re-derive rather than render an orphaned lens.
  if (state.catalogueEditMode && state.editingDetail
      && (!capabilityView || !state.editingDetail.capabilities.includes(capabilityView))) {
    capabilityView = firstGrantedCapability(state.editingDetail.capabilities);
  }

  const detail = state.catalogueEditMode && state.editingDetail ? state.editingDetail : getObjectiveDetail(name);
  const h = resolveODHierarchy(od);
  const goalCat = h.categoryDisplay;
  const aim = h.subcategoryDisplay;
  const isEdit = state.catalogueEditMode;

  let tabContent = "";
  const items = state.catalogueDetailTab === "attainment" ? detail.attainment : detail.prompts;
  const fieldPrefix = state.catalogueDetailTab === "attainment" ? "att" : "pr";
  const isPromptsTab = state.catalogueDetailTab === "prompts";
  // OMON-068 follow-up: in edit mode, the librarian can flip prompts
  // between raw markdown textareas and the rendered fold preview via
  // a header button. The flag is preserved across tab-switches; it
  // takes effect only on the prompts tab.
  const showRendered = isPromptsTab && (!isEdit || state.catalogueEditPreview);
  const collapsedSet = state.guidanceFoldState.get(name) ?? new Set<string>();

  // OMON-068: render the prompts tab through `guidanceParser` +
  // `guidanceRenderer` whenever the librarian wants to see the fold UI
  // — that's view mode always, plus edit mode with preview toggled on.
  // Edit mode without preview shows raw markdown textareas. Attainment
  // scale text and SMART goal stay as literal strings.
  tabContent = items.map((text, i) => {
    // 2026-06-10 (Ed): per-level preview in edit mode on the prompts tab —
    // the level renders the fold preview instead of its textarea when toggled.
    const levelInPreview = isEdit && isPromptsTab && !guidanceEditKeys.has(String(i));
    let body: string;
    if (showRendered || levelInPreview) {
      // Empty parse falls back to the raw text (see guidancePreviewHtml).
      body = guidancePreviewHtml(parseGuidance(text), i + 1, collapsedSet, text);
    } else if (isEdit) {
      // OMON-098: prompts textareas hide the stored H1 + subtitle
      // header (parser drops them at render time anyway) so the
      // librarian can't inadvertently break the convention. Attainment
      // textareas show their full content (no header convention).
      const editableText = isPromptsTab ? stripGuidanceHeader(text) : text;
      body = `<textarea class="level-text-edit" data-edit-field="${fieldPrefix}-${i}">${escapeHtml(editableText)}</textarea>`;
    } else {
      body = `<div class="level-text">${escapeHtml(text)}</div>`;
    }
    // Edit | Preview segmented mini-buttons per level (prompts tab, edit mode
    // only — mirrors the plan-builder's OMON-135 write/preview tabs).
    const modeButtons = isEdit && isPromptsTab && !showRendered
      ? editPreviewToggleHtml(String(i), levelInPreview)
      : "";
    return `
      <div class="level-row">
        <div class="level-num">${i + 1}</div>
        <div class="level-content">
          <div class="level-label" style="display:flex;align-items:center;gap:8px;">${LEVEL_NAMES[i]}${modeButtons}</div>
          ${body}
        </div>
      </div>
    `;
  }).join("");

  // Ed 2026-09-29: carer guidance is ONE text the author owns — one box and the
  // carer's screen beside it, replacing the five per-level boxes. The legacy
  // `documentation[]` is no longer edited here (still written back unchanged).
  if (isPromptsTab) {
    tabContent = guidanceEditorHtml(
      "editCarerGuidanceText", detail.guidanceText, isEdit,
      "# Before you start\n…\n\n# Level 1 · Fully supported\n## Your role\n…",
    );
  }

  // Zammad #49088: body of the M capability's active tab. The prompt tab is
  // the OMON-174 prompt exactly as before. The guidance tab is the SAME
  // one-box editor + carer preview as a goal's (Ed 2026-09-29: one set of
  // rules for everything a librarian writes). Legacy `##`-sectioned texts
  // open with their sections as `#` folds (eventGuidanceText).
  let monitoredTabContent: string;
  if (state.catalogueMonitoredTab === "guidance") {
    monitoredTabContent = guidanceEditorHtml(
      "editMonitoredEventGuidance", detail.monitoredEventGuidance, isEdit,
      "# Before you start\n…\n\n# What to record\n…\n\n# When and how to escalate\n1. …",
    );
  } else {
    monitoredTabContent = isEdit
      ? `<textarea class="smart-goal-edit" id="editMonitoredEventPrompt" placeholder="What should the carer know and do when handling this monitored event?">${escapeHtml(detail.monitoredEventPrompt)}</textarea>`
      : `<div class="smart-goal-box">${escapeHtml(detail.monitoredEventPrompt) || `<span style="color:#6b7280;">No prompt authored yet.</span>`}</div>`;
    // OMON-232 (Ed, 2026-10-01): a fact with parts (e.g. the ABC chart) gets
    // one default prompt per part, under the general prompt. Saved on blur,
    // straight onto that part's `_prompt` (partPromptSave), nothing else.
    monitoredTabContent += partsOf(od).map((p) => `
      <div style="margin-top:12px;">
        <div style="font-size:13px;font-weight:700;color:#374151;margin-bottom:4px;">${escapeHtml(p.label)}</div>
        ${isEdit
          ? `<textarea class="smart-goal-edit" data-part-prompt="${escapeHtml(p.code)}" placeholder="What should the carer write here?">${escapeHtml(p.defaultPrompt)}</textarea>`
          : `<div class="smart-goal-box">${escapeHtml(p.defaultPrompt) || `<span style="color:#6b7280;">No prompt for this part yet.</span>`}</div>`}
      </div>`).join("");
  }

  const currentTitle = isEdit && state.editingDetail ? state.editingDetail.title : od.title;

  // Category + aim pickers (edit mode only). Category dropdown lists all
  // active categories; aim dropdown cascades to show subcategories within the selected
  // category. Changing category triggers a re-render so the aim list refreshes.
  let pickerHtml = "";
  if (isEdit && state.editingDetail) {

    const caps = state.editingDetail.capabilities;
    // OMON-166 Part 2.5: big stacked capability buttons (code + plain-English
    // label), matching the locked mock. Colour per capability: D=purple,
    // M=blue, S=amber. Class stays `cap-pill` so the existing toggle handler binds.
    const CAP_META: Record<string, { label: string; bg: string; fg: string }> = {
      developmental: { label: "Developmental", bg: "#ddd6fe", fg: "#7c3aed" },
      monitored: { label: "Monitored", bg: "#eff6ff", fg: "#1e40af" },
      safety: { label: "Safety", bg: "#fde68a", fg: "#92400e" },
    };
    // 2026-07-08: single-select capability. A trackable IS exactly one of
    // D / M / S — the pills are radio-style: the selected one is filled, the
    // others dimmed. Clicking a pill REPLACES the selection (handler below);
    // there is no grant/revoke ✓ and no separate view lens (with one capability,
    // its content is simply what shows).
    const capPill = (code: string, abbr: string) => {
      const selected = caps.includes(code);
      const m = CAP_META[code];
      const box = selected
        ? `border:1px solid ${m.fg};background:${m.bg};color:${m.fg};`
        : `border:1px solid #e5e7eb;color:#9ca3af;`;
      return `<span class="cap-pill${selected ? " on" : ""}" data-cap="${code}" role="radio" aria-checked="${selected}" title="${code}${selected ? " — selected" : " — click to select"}" style="flex:1;position:relative;text-align:center;cursor:pointer;user-select:none;border-radius:8px;padding:8px 4px;${box}"><span style="display:block;font-weight:700;font-size:13px;">${abbr}</span><span style="font-size:11px;${selected ? "opacity:.8;" : "color:#6b7280;"}">${m.label}</span></span>`;
    };
    // OMON-166 Part 2.5: Status Active/Retired segmented control.
    const status = state.editingDetail.status || "active";
    const statusSeg = (value: string, label: string) => {
      const on = status === value;
      return `<div class="status-seg-opt" data-status="${value}" style="flex:1;text-align:center;padding:6px;cursor:pointer;font-size:13px;${on ? "background:#7c3aed;color:#fff;font-weight:600;" : ""}">${label}</div>`;
    };
    // OMON-166 Part 2.5: friendly labels (the "modalities titled as in the
    // visible dropdown" Ed asked for). Value stays the canonical enum; only the
    // visible text is humanised — matches the locked mock's #iKind options.
    // OMON-232: the list lives in recording-kinds.ts; `incident` (ABC chart)
    // is offered off production only until the carer app can record it.
    const RECORDING_KINDS = recordingKindChoices(firestoreDatabaseId, state.editingDetail.recordingKind);
    const kindOptions = RECORDING_KINDS.map(([k, label]) =>
      `<option value="${k}" ${k === state.editingDetail!.recordingKind ? "selected" : ""}>${escapeHtml(label)}</option>`
    ).join("");
    // OMON-172: librarian per-fact policy toggles (Targetable / Escalatable /
    // Mandatory), matching the locked librarian mock's toggle-row layout
    // (title + sub-label + switch). Librarian sets the definition default; the
    // planner overrides per client (definition-vs-instance split). `data-policy`
    // drives the toggle handler; the switch is a CSS-styled span (no checkbox so
    // we keep the always-edit auto-save click path consistent with the pills).
    const ed = state.editingDetail;
    const policyToggle = (key: "targetable" | "escalatable" | "mandatory" | "rolesLocked", title: string, sub: string) => {
      const on = ed[key] === true;
      return `<div class="policy-togline" style="display:flex;align-items:center;gap:8px;padding:8px 2px;border-top:1px solid #f3f4f6;">
        <div style="flex:1;"><div style="font-size:13px;font-weight:600;color:#111827;">${title}</div><div style="font-size:11px;color:#6b7280;">${sub}</div></div>
        <span class="policy-toggle" data-policy="${key}" role="switch" aria-checked="${on}" title="${on ? "On" : "Off"}" style="flex:0 0 auto;width:38px;height:21px;border-radius:11px;cursor:pointer;user-select:none;position:relative;background:${on ? "#16a34a" : "#d1d5db"};transition:background .15s;">
          <span style="position:absolute;top:2px;left:${on ? "19px" : "2px"};width:17px;height:17px;border-radius:50%;background:#fff;transition:left .15s;box-shadow:0 1px 2px rgba(0,0,0,.25);"></span>
        </span>
      </div>`;
    };
    // OMON-170: stack the placement fields one-per-row, Status first, then
    // Category, then Sub-category (was a single flex-wrap row that broke the
    // controls across two lines with a dangling "Sub-category" label).
    const metaLabel = "font-size:13px;color:#374151;width:92px;flex:0 0 auto;";
    pickerHtml = `
      <div class="detail-meta" style="display:flex;gap:8px;align-items:center;margin-bottom:6px;">
        <label style="${metaLabel}">Status</label>
        <div class="status-seg" style="display:flex;border:1px solid #e5e7eb;border-radius:7px;overflow:hidden;min-width:150px;">
          ${statusSeg("active", "Active")}${statusSeg("retired", "Retired")}
        </div>
      </div>
      <div style="margin-top:8px;">
        <label style="display:block;font-size:11px;font-weight:600;letter-spacing:.03em;text-transform:uppercase;color:#6b7280;margin-bottom:4px;">Capabilities — what this fact can support</label>
        <div style="display:flex;gap:8px;">
          ${capPill("developmental", "D")}${capPill("monitored", "M")}${capPill("safety", "S")}
        </div>
      </div>
      ${
        // Per-type field manifest (Ed, 2026-06-10): the Developmental world IS
        // production today — no recording-method dropdown (D is the 1–5
        // attainment scale by definition) and no policy flags. Recording method
        // + policy belong to the Monitored / Safety worlds. The baby-steps
        // bridge: each type shows a coherent field set; the data stays one
        // ObservationDefinition with capability codings.
        capabilityView === "monitored" || capabilityView === "safety"
          ? `<div class="detail-meta" style="gap:8px;align-items:center;flex-wrap:wrap;margin-top:8px;">
        <label style="font-size:13px;color:#374151;">Recording method</label>
        <select id="editRecordingKind" style="padding:2px 6px;">${kindOptions}</select>
      </div>
      <div style="margin-top:12px;">
        <label style="display:block;font-size:11px;font-weight:600;letter-spacing:.03em;text-transform:uppercase;color:#6b7280;margin-bottom:2px;">Policy defaults — every planner gets both controls; this sets how they arrive</label>
        ${policyToggle("escalatable", "Escalatable by default", "New plan entries arrive with the carer’s “Notify office” tickbox switched on")}
      </div>`
          : ""
      }
      ${
        // ADR-0035 am.8: who owes it, for EVERY item (goals included). The
        // default seeds each new plan item; the lock stops planners changing it.
        `<div style="margin-top:12px;">
        <label style="display:block;font-size:11px;font-weight:600;letter-spacing:.03em;text-transform:uppercase;color:#6b7280;margin-bottom:2px;">Owed by default — which carer roles must complete it each shift</label>
        <div style="display:flex;gap:12px;flex-wrap:wrap;padding:6px 2px;">${SHIFT_ROLES.map((r) =>
          `<label style="display:inline-flex;align-items:center;gap:4px;font-size:13px;color:#374151;cursor:pointer;"><input type="checkbox" data-cat-role="${r.code}" ${ed.shiftRolesDefault.includes(r.code) ? "checked" : ""}> ${escapeHtml(r.display)}</label>`).join("")}</div>
        ${policyToggle("rolesLocked", "Lock for planners", "Planners can’t change who owes this, and the lock applies to existing plans too (the Shift log)")}
      </div>`
      }
    `;
  }

  // OMON-166 Part 2: render into the right-hand inspector pane when it exists
  // (Manage-Hierarchy 50/50 layout); otherwise the whole catalogue pane (classic
  // drill-in). Event bindings below use catalogueList.querySelector, which finds
  // the elements in either case (the inspector pane is inside catalogueList).
  // OMON-166 Part 2.5: inside the inspector pane the editor is ALWAYS-EDIT and
  // auto-saves, so it shows no Save/Cancel/Edit/Delete buttons (Delete moves to
  // the Part 4 context menu). Subtitle mirrors the locked mock.
  const variantCount = od.hasMember?.length || 0;
  const subtitle = `Trackable · in ${escapeHtml(goalCat)}${variantCount ? ` · ${variantCount} variant${variantCount === 1 ? "" : "s"}` : ""}`;

  const detailTarget = (catalogueList.querySelector("#catInspectorPane") as HTMLElement | null) || catalogueList;
  detailTarget.innerHTML = `
    <div class="detail-panel">
      <div class="plan-header">
        <div style="flex:1;">
          ${isEdit
            ? `<input type="text" id="editTitle" data-editing-name="${escapeHtml(name)}" class="title-edit" value="${escapeHtml(currentTitle)}" style="font-size:17px;font-weight:600;width:100%;padding:6px 8px;margin-bottom:4px;border:1px solid #d1d5db;border-radius:4px;">`
            : `<h2 style="font-size:17px;margin-bottom:4px;">${escapeHtml(currentTitle)}</h2>`
          }
          ${inInspectorPane ? `<div class="insp-sub" style="color:#6b7280;font-size:11px;margin:2px 2px 8px;">${subtitle}</div>` : ""}
          ${isEdit ? pickerHtml : `
            <div class="detail-meta">
              <span>${escapeHtml(goalCat)}</span>
              <span>${escapeHtml(aim)}</span>
              <span>Scale: 1-5</span>
            </div>
          `}
        </div>
      </div>

      ${
        // OMON-166: SMART goal is the Developmental capability's content.
        // 2026-06-10 (Ed): in edit mode it shows only when D is the SELECTED
        // VIEW (single-lens pills), not merely granted; view mode still shows
        // per granted coding. Replaces the legacy `_measurementType` test.
        (isEdit && state.editingDetail
          ? state.editingDetail.capabilities.includes("developmental") && capabilityView === "developmental"
          : trackableCapabilities(od).includes("developmental"))
          ? `<div class="detail-section">
        <h4>What does good look like</h4>
        ${isEdit
          ? `<textarea class="smart-goal-edit" id="editSmartGoal">${escapeHtml(detail.smartGoal)}</textarea>`
          : `<div class="smart-goal-box">${escapeHtml(detail.smartGoal)}</div>`
        }
      </div>`
          : ""
      }

      ${
        // OMON-174: the monitored-event prompt is the Monitored capability's
        // content, mirroring SMART-goal-follows-D above. Canonical text the
        // planner snapshot-copies and tunes per client at plan-add time.
        // 2026-06-10 (Ed): edit mode gates on the selected VIEW like SMART.
        // Zammad #49088 (Ed, 2026-09-24): the M capability gets the same tabs
        // row as D — "Monitored Event Prompt" | "Carer Guidance Prompts" (one
        // guidance text per event, not per level). Own tab state
        // (`catalogueMonitoredTab`) so it never disturbs the D tabs.
        (isEdit && state.editingDetail
          ? state.editingDetail.capabilities.includes("monitored") && capabilityView === "monitored"
          : trackableCapabilities(od).includes("monitored"))
          ? `<div class="tabs-row">
        <button class="detail-tab ${state.catalogueMonitoredTab === "prompt" ? "active" : ""}" data-monitored-tab="prompt">
          Monitored Event Prompt
        </button>
        <button class="detail-tab ${state.catalogueMonitoredTab === "guidance" ? "active" : ""}" data-monitored-tab="guidance">
          Carer Guidance
        </button>
      </div>
      <div class="detail-section">
        ${monitoredTabContent}
      </div>`
          : ""
      }

      ${inInspectorPane ? `<div class="note-chip" style="display:flex;align-items:center;gap:8px;background:#f9fafb;border:1px solid #eff6ff;border-radius:8px;padding:8px 12px;font-size:13px;color:#1e40af;margin:12px 0;"><b style="font-weight:700;">＋ Free-text note</b> — always available to the carer alongside the score, whatever the recording type.</div>` : ""}

      ${
        // Per-type field manifest (Ed, 2026-06-10): the Attainment Levels and
        // 5-level Carer Guidance tabs are Developmental-world concepts (the
        // production-today field set). In edit mode they show only under the D
        // lens; the M world's carer text is the Monitored Event Prompt above.
        // View mode (classic drill-in read view) keeps the tabs as before.
        !isEdit || capabilityView === "developmental"
          ? `<div class="tabs-row">
        <button class="detail-tab ${state.catalogueDetailTab === "attainment" ? "active" : ""}" data-detail-tab="attainment">
          Attainment Levels
        </button>
        <button class="detail-tab ${state.catalogueDetailTab === "prompts" ? "active" : ""}" data-detail-tab="prompts">
          Carer Guidance
        </button>
      </div>

      ${tabContent}`
          : ""
      }
    </div>
  `;

  // ADR-0048: on the TRAINING build only, an admin or practice-author gets a
  // "Promote to production" section at the foot of the item (Ed's call: one
  // item, one deliberate act). Appended rather than woven into the template
  // above, so the promotion path can be read — and removed — in one place.
  renderPromoteSection(
    detailTarget.querySelector<HTMLElement>(".detail-panel") ?? detailTarget,
    "_observationDefinition",
    name,
    od._provenance,
  );

  // ADR-0048 phase 2, the mirror: on the PRODUCTION build, an item that
  // arrived by promotion says so, and an admin or librarian can take it back
  // out. Renders nothing on anything production authored itself — which is
  // almost everything — so this is invisible until it matters.
  renderProvenanceSection(
    detailTarget.querySelector<HTMLElement>(".detail-panel") ?? detailTarget,
    "_observationDefinition",
    name,
    od._provenance,
    () => renderCatalogue(),
  );

  // OMON-068: track librarian's per-section fold toggles in
  // session-only state. The Set holds keys of *collapsed* sections;
  // empty Set ⇒ default-expanded. No re-render needed — the
  // `<details>` element handles the visual toggle natively.
  catalogueList.querySelectorAll<HTMLDetailsElement>("details[data-fold-key]").forEach((d) => {
    d.addEventListener("toggle", () => {
      const key = d.dataset.foldKey;
      if (!key) return;
      const goalSet = state.guidanceFoldState.get(name) ?? new Set<string>();
      if (d.open) {
        goalSet.delete(key);
      } else {
        goalSet.add(key);
      }
      state.guidanceFoldState.set(name, goalSet);
    });
  });

  // Bind events

  // OMON-166 Part 2.5: in the always-edit inspector, a STRUCTURAL change
  // (capability toggle, recording method, category/aim move, status) persists
  // immediately via a silent save, then re-renders so the tree's badges/buckets
  // reflect it. In classic drill-in edit mode, persistence still waits for the
  // explicit Save button — so this only fires inside the inspector pane.
  const applyStructural = async () => {
    if (inInspectorPane) await saveEditingObjective(name, { silent: true });
    renderCatalogue();
  };
  // Text edits (title, SMART, attainment, prompts) auto-save on BLUR with no
  // re-render — re-rendering mid-edit would destroy the focused textarea. The
  // blur fires before any subsequent click/tab/row-select, so it's the safety
  // net that stops captured-but-unpersisted edits being lost on re-seed.
  const autoSaveText = () => {
    if (!inInspectorPane || !state.editingDetail) return;
    captureEditingDetailFromForm();
    void saveEditingObjective(name, { silent: true });
  };


  // OMON-188: the Category / Sub-category dropdowns were removed — placement is
  // now driven entirely by dragging in the left tree (drag into a category, a
  // sub-category, or a parent fact's variant group). recategoriseFact /
  // makeVariantOf write Firestore + cache directly and the tree repaints.

  // OMON-166 capability pills, reworked 2026-06-10 (Ed) as a single-select view
  // switch. Pill click: granted → just switch the lens (no save — pure view);
  // ungranted → grant (data write, as before) AND switch the lens. The ✓ on a
  // granted pill revokes (the old toggle-off path); revoking the viewed
  // capability falls back to the first remaining granted one.
  catalogueList.querySelectorAll<HTMLElement>(".cap-pill").forEach((pill) => {
    pill.addEventListener("click", () => {
      if (!state.editingDetail) return;
      const code = pill.dataset.cap;
      if (!code) return;
      captureEditingDetailFromForm();
      const caps = state.editingDetail.capabilities;
      // Single-select (2026-07-08): a trackable is exactly one of D/M/S. Clicking
      // a pill makes it THE capability, replacing any prior selection. Clicking
      // the one already selected is a no-op (don't churn a save). There is no
      // revoke-to-empty — you switch to another capability, never to none.
      if (caps.length === 1 && caps[0] === code) return;
      state.editingDetail.capabilities = [code];
      capabilityView = code;
      void applyStructural();
    });
  });

  // OMON-166 Part 2.5: Status Active/Retired segmented control (inspector only).
  catalogueList.querySelectorAll<HTMLElement>(".status-seg-opt").forEach((opt) => {
    opt.addEventListener("click", () => {
      if (!state.editingDetail) return;
      const value = opt.dataset.status;
      if (!value || value === state.editingDetail.status) return;
      captureEditingDetailFromForm();
      state.editingDetail.status = value;
      void applyStructural();
    });
  });

  catalogueList.querySelector("#editRecordingKind")?.addEventListener("change", (e) => {
    if (!state.editingDetail) return;
    captureEditingDetailFromForm();
    state.editingDetail.recordingKind = (e.target as HTMLSelectElement).value;
    void applyStructural();
  });

  // OMON-172: librarian per-fact policy toggles (Targetable / Escalatable /
  // Mandatory). Flip the flag + silent-save + re-render (so the tree's
  // escalatable badge appears/disappears). Turning Escalatable on with no
  // threshold yet seeds the default of 5; the threshold has no inspector UI
  // (removed 2026-06-10, Ed) — it lives in data only until the planner
  // override surface owns it.
  catalogueList.querySelectorAll<HTMLElement>(".policy-toggle").forEach((tog) => {
    tog.addEventListener("click", () => {
      if (!state.editingDetail) return;
      const key = tog.dataset.policy as "targetable" | "escalatable" | "mandatory" | "rolesLocked" | undefined;
      if (!key) return;
      captureEditingDetailFromForm();
      state.editingDetail[key] = !state.editingDetail[key];
      if (key === "escalatable" && state.editingDetail.escalatable && state.editingDetail.escalationThreshold === null) {
        state.editingDetail.escalationThreshold = 5;
      }
      void applyStructural();
    });
  });
  // ADR-0035 am.8: the librarian's default role ticks.
  catalogueList.querySelectorAll<HTMLInputElement>("[data-cat-role]").forEach((cb) => {
    cb.addEventListener("change", () => {
      if (!state.editingDetail) return;
      captureEditingDetailFromForm();
      const role = cb.dataset.catRole as ShiftRoleCode;
      const cur = state.editingDetail.shiftRolesDefault;
      const next = cur.includes(role) ? cur.filter((r) => r !== role) : [...cur, role];
      state.editingDetail.shiftRolesDefault = ALL_SHIFT_ROLE_CODES.filter((c) => next.includes(c));
      state.editingDetail.mandatory = state.editingDetail.shiftRolesDefault.length > 0;
      void applyStructural();
    });
  });
  // 2026-06-10 (Ed): per-level Edit | Preview toggle on the prompts tab.
  // Capture (and in the inspector, persist) the in-flight typing BEFORE the
  // re-render destroys the textarea — same discipline as the tab switch below.
  catalogueList.querySelectorAll<HTMLElement>("[data-prompt-mode]").forEach((btn) => {
    btn.addEventListener("click", () => {
      if (!state.editingDetail) return;
      // A D level index ("0".."4") or "event" (monitored-event guidance).
      const key = btn.dataset.promptLevel;
      if (!key) return;
      captureEditingDetailFromForm();
      if (inInspectorPane) void saveEditingObjective(name, { silent: true });
      if (btn.dataset.promptMode === "preview") guidanceEditKeys.delete(key);
      else guidanceEditKeys.add(key);
      renderCatalogue();
    });
  });
  // Edit boxes grow to fit their text rather than scrolling inside — every
  // textarea in the item editor (What does good look like, Monitored Event
  // Prompt, event Carer Guidance, attainment levels, carer guidance prompts).
  autosizeTextareas(catalogueList, "textarea.level-text-edit, textarea.smart-goal-edit");

  catalogueList.querySelectorAll<HTMLElement>("[data-detail-tab]").forEach((btn) => {
    btn.addEventListener("click", () => {
      // Capture in-flight typing before re-rendering — without this,
      // edits made on one tab are lost when switching to the other
      // (the textareas get destroyed on re-render). Pre-existing
      // bug surfaced while wiring up OMON-068's preview toggle.
      // OMON-166 Part 2.5: in the inspector, persist before the re-render so the
      // captured edits aren't lost if the user then selects another fact.
      if (inInspectorPane && state.editingDetail) {
        captureEditingDetailFromForm();
        void saveEditingObjective(name, { silent: true });
      } else {
        captureEditingDetailFromForm();
      }
      state.catalogueDetailTab = btn.dataset.detailTab as "attainment" | "prompts";
      renderCatalogue();
    });
  });
  // Zammad #49088: the M capability's tabs — same capture-then-persist
  // discipline as the D tabs above.
  catalogueList.querySelectorAll<HTMLElement>("[data-monitored-tab]").forEach((btn) => {
    btn.addEventListener("click", () => {
      captureEditingDetailFromForm();
      if (inInspectorPane && state.editingDetail) void saveEditingObjective(name, { silent: true });
      state.catalogueMonitoredTab = btn.dataset.monitoredTab === "guidance" ? "guidance" : "prompt";
      renderCatalogue();
    });
  });

  // OMON-166 Part 2.5: text fields auto-save on blur (no re-render). Covers the
  // title, SMART goal, and every attainment/prompt textarea. `autoSaveText`
  // no-ops outside the inspector pane, so classic drill-in edit is unchanged.
  catalogueList.querySelector("#editTitle")?.addEventListener("blur", autoSaveText);
  // 2026-06-16 (Ed): live-sync right→left. As the title is typed in the inspector,
  // mirror it into the matching left-tree row's name (no save/refresh needed).
  // Targets the `.obj-name` span so the grip/badges are preserved; reverse
  // direction (inline rename → inspector) lives in tree.ts.
  catalogueList.querySelector<HTMLInputElement>("#editTitle")?.addEventListener("input", (e) => {
    const v = (e.target as HTMLInputElement).value;
    if (state.editingDetail) state.editingDetail.title = v;
    const name = state.catalogueSelectedGoal;
    if (!name) return;
    for (const row of catalogueList.querySelectorAll<HTMLElement>(`.objective-row[data-obj-title]`)) {
      if (row.dataset.objTitle !== name) continue;
      const nameSpan = row.querySelector<HTMLElement>(".obj-name");
      if (nameSpan) nameSpan.textContent = v;
      break;
    }
  });
  catalogueList.querySelector("#editSmartGoal")?.addEventListener("blur", autoSaveText);
  catalogueList.querySelector("#editMonitoredEventPrompt")?.addEventListener("blur", autoSaveText);
  // OMON-232: per-part default prompts save on blur, one part at a time.
  catalogueList.querySelectorAll<HTMLTextAreaElement>("[data-part-prompt]").forEach((ta) => {
    ta.addEventListener("blur", () => void partPromptSave(name, ta.dataset.partPrompt ?? "", ta.value));
  });
  catalogueList.querySelector("#editMonitoredEventGuidance")?.addEventListener("blur", autoSaveText);
  wireGuidanceEditor(catalogueList, "editCarerGuidanceText", (v) => {
    if (state.editingDetail) state.editingDetail.guidanceText = v;
  });
  wireGuidanceEditor(catalogueList, "editMonitoredEventGuidance", (v) => {
    if (state.editingDetail) state.editingDetail.monitoredEventGuidance = v;
  });
  catalogueList.querySelector("#editCarerGuidanceText")?.addEventListener("blur", autoSaveText);
  catalogueList.querySelectorAll<HTMLElement>("[data-edit-field]").forEach((el) => {
    el.addEventListener("blur", autoSaveText);
  });
}

export async function saveEditingObjective(
  name: string,
  opts: { silent?: boolean; suppressUndo?: boolean } = {},
) {
  if (!state.editingDetail) return;
  const startEpoch = editSessionEpoch;
  // OMON-232 (race fix, 2026-06-29): bind the edit buffer to a LOCAL now. This
  // save is async and fire-and-forget (autoSaveText blurs do `void save…`). A
  // fact-switch during an `await getDoc` yield calls beginEditSession, which
  // REPLACES state.editingDetail with the newly-selected fact's buffer. Reading
  // the global after the yield wrote the NEW fact's content onto THIS `name`'s
  // doc (the 3rd dental cross-fact overwrite). The captured reference keeps
  // pointing at the buffer this save started with, bound to `name`.
  const editing = state.editingDetail;

  // OMON-192 desync guard: refuse to write if the edit buffer (editing,
  // bound to `editingForName` when it was seeded) is not for THIS `name`. A
  // mismatch is exactly how editing one fact overwrote another — the 2026-06-26
  // dental-hygiene incident, where electric/flossing content was written under
  // the manual-toothbrush id. Fail loud rather than corrupt a different fact.
  // (The durable fix is opaque ids + atomic seed; this is the stop-the-bleeding
  // assertion until OMON-192 (B) lands.)
  if (editingForName !== name) {
    console.error(
      `[catalogue] save aborted: edit buffer is bound to "${editingForName}", ` +
        `not "${name}" — refusing to avoid a cross-fact overwrite.`,
    );
    statusEl.textContent =
      "Save aborted — the editor was out of sync. Reopen the entry and try again.";
    return;
  }

  // OMON-182: pre-write snapshot from the (still-unmutated) local cache — the
  // undo toast restores exactly this. Same construction the explicit-Save path
  // used before the always-edit unification. suppressUndo marks undo-writes
  // themselves so restoring doesn't re-toast.
  const odForSnap = state.catalogueData.get(name);
  const snapSeed = getObjectiveDetail(name);
  const snapH = odForSnap ? resolveODHierarchy(odForSnap) : { categoryCode: "", subcategoryCode: "" };
  const undoSnapshot = odForSnap ? {
    title: odForSnap.title,
    smartGoal: snapSeed.smartGoal,
    monitoredEventPrompt: snapSeed.monitoredEventPrompt,
    monitoredEventGuidance: snapSeed.monitoredEventGuidance,
    guidanceText: snapSeed.guidanceText,
    attainment: [...snapSeed.attainment],
    prompts: [...snapSeed.prompts],
    categoryCode: snapH.categoryCode,
    subcategoryCode: snapH.subcategoryCode,
    capabilities: trackableCapabilities(odForSnap),
    recordingKind: odForSnap._recordingKind || "attainment-5",
    status: (odForSnap.status === "retired" ? "retired" : "active") as "active" | "retired",
    ...policyFlagsFromOd(odForSnap),
  } : null;

  // Read values from inputs
  const titleEl = catalogueList.querySelector("#editTitle") as HTMLInputElement | null;
  // Form-provenance guard (3rd dental cross-fact overwrite, 2026-06-29): the live
  // inspector form must belong to `name`. If the pane repainted to a sibling fact
  // while a stale/queued save still targets this one, abort rather than write this
  // fact's form values onto a different doc. Complements the editingForName check.
  if (titleEl && titleEl.dataset.editingName && titleEl.dataset.editingName !== name) {
    console.error(
      `[catalogue] save aborted: form is for "${titleEl.dataset.editingName}", not ` +
        `"${name}" — refusing a cross-fact overwrite.`,
    );
    statusEl.textContent =
      "Save aborted — the editor was out of sync. Reopen the entry and try again.";
    return;
  }
  if (titleEl) editing.title = titleEl.value.trim();

  const smartGoalEl = catalogueList.querySelector("#editSmartGoal") as HTMLTextAreaElement | null;
  if (smartGoalEl) editing.smartGoal = smartGoalEl.value;

  for (let i = 0; i < 5; i++) {
    const attEl = catalogueList.querySelector(`[data-edit-field="att-${i}"]`) as HTMLTextAreaElement | null;
    const prEl = catalogueList.querySelector(`[data-edit-field="pr-${i}"]`) as HTMLTextAreaElement | null;
    if (attEl) editing.attainment[i] = attEl.value;
    // OMON-098: textarea contains the editable tail only; reconstruct
    // the stored shape (H1 + subtitle) from current title + subcategory.
    if (prEl) editing.prompts[i] = wrapPromptWithHeader(prEl.value);
  }

  if (!editing.title) {
    statusEl.textContent = "Title cannot be empty.";
    return;
  }

  // Resolve new category/aim display from the hierarchy map for denormalised
  // writing. Codes are the canonical reference; display is a hint for
  // fault-tolerant rendering (see resolveODHierarchy fallback chain).
  const newCatNode = state.goalCategories.get(editing.categoryCode);
  const newAimDocId = `${editing.categoryCode}__${editing.subcategoryCode}`;
  const newAimNode = state.goalSubcategories.get(newAimDocId);

  if (!newCatNode) {
    statusEl.textContent = "Selected category not found — refresh and try again.";
    return;
  }
  if (editing.subcategoryCode && !newAimNode) {
    statusEl.textContent = "Selected sub-category not found — refresh and try again.";
    return;
  }

  try {
    // OMON-232: a fact-switch during the async form read/build bumps the epoch;
    // refuse to write this (now stale) content onto whatever is selected now.
    if (editSessionEpoch !== startEpoch) {
      console.error("[catalogue] save aborted: edit session changed mid-save");
      return;
    }
    // Rebuild category[] on both OD and AD via the extracted pure helper
    // in `./category.ts` (see that module for the defensive-default
    // rationale that pins the PR #435363 sparse-array regression).
    const categoryFromPrior = (prior: unknown[]) => buildCategoryArray({
      prior,
      categoryCode: editing.categoryCode,
      subcategoryCode: editing.subcategoryCode,
      categoryDisplay: newCatNode.display,
      subcategoryDisplay: newAimNode?.display || "",
    });

    // Update ActivityDefinition: documentation (carer prompts) + title (paired rename) + category
    const adRef = doc(db, "_activityDefinition", name);
    const adSnap = await getDoc(adRef);
    if (adSnap.exists()) {
      const adData = adSnap.data();
      // OMON-242 blank-prompt guard (2026-06-30 toothbrush data-loss): the
      // cross-FIELD overwrite writes editing.prompts as header-only (empty tails)
      // over stored content. The name-based #5b21b6/#16a34a guards don't catch it (same
      // fact). If every incoming prompt is effectively empty but stored
      // documentation still has real bodies, DROP documentation from this write
      // rather than wipe it — preserve stored and tell the user.
      const incomingPromptsAllEmpty = editing.prompts.every(
        (p) => stripGuidanceHeader(p || "").trim() === "",
      );
      const storedHasPrompts = (adData.documentation || []).some(
        (d: { display?: string }) => stripGuidanceHeader(d.display || "").trim() !== "",
      );
      const adUpdate: Record<string, unknown> = {
        title: editing.title,
        category: categoryFromPrior(adData.category || []),
      };
      if (incomingPromptsAllEmpty && storedHasPrompts) {
        console.error(
          `[catalogue] BLANK-PROMPT GUARD: refusing to wipe carer-guidance prompts on ` +
            `"${name}" — all incoming prompts empty but stored content exists. Preserved.`,
        );
        statusEl.textContent =
          "Carer-guidance prompts NOT overwritten — the edit would have blanked existing content. Reopen the entry.";
      } else {
        adUpdate.documentation = editing.prompts.map((text, i) => ({
          type: "documentation",
          label: LEVEL_NAMES[i],
          display: text,
        }));
      }
      // Ed 2026-09-29: write the one guidance text only when the author changed
      // it from what they were shown (saved text, else the composed levels) — an
      // untouched item keeps composing from its levels, nothing is materialised.
      if (guidanceTextChanged(editing.guidanceText, adData)) {
        adUpdate._carerGuidanceText = editing.guidanceText;
      }
      await updateDoc(adRef, adUpdate);
    } else if (editing.capabilities.includes("developmental")) {
      // OMON-242 prevention (2026-07-07): a developmental fact MUST have a paired
      // carer-guidance ActivityDefinition — it's the content of that capability.
      // Facts minted by the trackables-v3-bulk migration, and facts granted
      // Developmental later in the inspector, never had one created, so
      // getObjectiveDetail falls back to hard-coded defaults forever and authored
      // guidance can't persist. Materialise it now from the current buffer
      // (preserving any in-flight edit; for a fresh grant the buffer is the
      // seeded starting content). This is the durable cure — the backfill only
      // remediates the ~42 already in prod; this stops new ones appearing.
      const newAd = {
        resourceType: "ActivityDefinition",
        url: `stc:activity/${name}`,
        name,
        title: editing.title,
        status: "active",
        category: setCapabilityCodings(categoryFromPrior([]), editing.capabilities),
        documentation: editing.prompts.map((text, i) => ({
          type: "documentation",
          label: LEVEL_NAMES[i],
          display: text,
        })),
        ...(editing.guidanceText.trim() ? { _carerGuidanceText: editing.guidanceText } : {}),
        active: true,
        meta: { security: metaSecurityFor(state.authClaims) },
      };
      await setDoc(adRef, newAd);
      state.activityData.set(name, newAd as unknown as ActivityDefinition);
    }

    // Update ObservationDefinition: title + code.coding display (denormalised) +
    // SMART goal template + qualifiedValue descriptions + category (for move).
    // Goals referencing this objective don't need an update — they resolve title
    // live via stable code per OMON-004 Decision 1.
    const odRef = doc(db, "_observationDefinition", name);
    const odSnap = await getDoc(odRef);
    let wroteMonitored: MonitoredTextWrites = {};
    if (odSnap.exists()) {
      const data = odSnap.data();
      const qv = data.qualifiedValue || [];
      // OMON-242 blank-attainment guard (the "vice versa" cross-field overwrite):
      // don't wipe attainment descriptions with all-empty incoming values when
      // stored ones exist.
      const incomingAttAllEmpty = editing.attainment.every((a) => (a || "").trim() === "");
      const storedAttHasContent = qv.some(
        (q: { context?: { description?: string } }) => (q.context?.description || "").trim() !== "",
      );
      if (incomingAttAllEmpty && storedAttHasContent) {
        console.error(
          `[catalogue] BLANK-ATTAINMENT GUARD: refusing to wipe attainment on "${name}" ` +
            `— all incoming levels empty but stored content exists. Preserved.`,
        );
        statusEl.textContent =
          "Attainment levels NOT overwritten — the edit would have blanked existing content. Reopen the entry.";
      } else {
        for (let i = 0; i < 5; i++) {
          if (qv[i]) {
            if (!qv[i].context) qv[i].context = { text: "" };
            qv[i].context.description = editing.attainment[i];
          }
        }
      }
      const codeCoding = data.code?.coding || [];
      if (codeCoding[0]) codeCoding[0].display = editing.title;
      // SMART goal template is a SOMA-only concept; don't write it on
      // monitored-event ODs (the UI also hides the section for them, so
      // editingDetail.smartGoal would be stale/empty anyway).
      // OMON-166: capabilities + recording method. Category is rebuilt by
      // buildCategoryArray (goal-cat/subcat, preserving capabilities) then has its
      // capability codings re-authored from the toggles. SMART goal is written
      // only when the Developmental capability is active.
      const isDevelopmental = editing.capabilities.includes("developmental");
      const odUpdate: Record<string, unknown> = {
        title: editing.title,
        "code.coding": codeCoding,
        category: setCapabilityCodings(
          categoryFromPrior(data.category || []),
          editing.capabilities,
        ),
        qualifiedValue: qv,
        _recordingKind: editing.recordingKind,
        // OMON-166 Part 2.5: FHIR ObservationDefinition.status (Active/Retired).
        status: editing.status || "active",
        // OMON-172: librarian per-fact policy flags (definition-layer defaults).
        _targetable: editing.targetable,
        _escalatable: editing.escalatable,
        // ADR-0035 am.8: the role default + lock; `_mandatory` its derived
        // shadow for readers that predate roles.
        _shiftRolesDefault: editing.shiftRolesDefault,
        _rolesLocked: editing.rolesLocked,
        _mandatory: editing.shiftRolesDefault.length > 0,
        // Threshold only meaningful while Escalatable; clear it otherwise so a
        // stale default can't linger on a no-longer-escalatable fact.
        _escalationThresholdDefault:
          editing.escalatable && editing.escalationThreshold !== null
            ? editing.escalationThreshold
            : deleteField(),
      };
      if (isDevelopmental) {
        odUpdate._smartGoalTemplate = editing.smartGoal;
      }
      // OMON-174: canonical monitored-event prompt — written only when the
      // Monitored capability is active, mirroring the SMART-goal gate above.
      // Zammad #49088 (Ed, 2026-09-24): prompt and event carer guidance are
      // both written only when the item is Monitored AND the text differs
      // from what is stored, so an untouched event never gains empty fields.
      if (editing.capabilities.includes("monitored")) {
        const monitoredWrites = changedMonitoredTextFields(editing, data);
        Object.assign(odUpdate, monitoredWrites);
        wroteMonitored = monitoredWrites;
      }
      await updateDoc(odRef, odUpdate);
    }

    // Refresh local data so the view updates without a reload
    const od = state.catalogueData.get(name);
    if (od) {
      od.title = editing.title;
      // Mirror the SMART goal write decision exactly (feedback_silent_success_stale_cache):
      // only when Developmental is active, matching the Firestore write above.
      if (editing.capabilities.includes("developmental")) {
        od._smartGoalTemplate = editing.smartGoal;
      }
      // OMON-174 / Zammad #49088: mirror the monitored-text write decision
      // exactly — only the fields actually written above.
      Object.assign(od, wroteMonitored);
      od._recordingKind = editing.recordingKind;
      od.status = editing.status || "active";
      // OMON-172: mirror the policy flags (feedback_silent_success_stale_cache).
      // Threshold field is removed from the cache object when not escalatable,
      // mirroring the deleteField() in the Firestore write above.
      od._targetable = editing.targetable;
      od._escalatable = editing.escalatable;
      od._shiftRolesDefault = editing.shiftRolesDefault;
      od._rolesLocked = editing.rolesLocked;
      od._mandatory = editing.shiftRolesDefault.length > 0;
      if (editing.escalatable && editing.escalationThreshold !== null) {
        od._escalationThresholdDefault = editing.escalationThreshold;
      } else {
        delete od._escalationThresholdDefault;
      }
      // OMON-059: mirror the qualifiedValue edits (attainment levels)
      // into the local cache. Without this the next render reads pre-edit
      // values from cache and the UI looks like the save failed silently
      // — even though the Firestore write succeeded.
      // OMON-242: mirror only when we actually wrote attainment — if the guard
      // above preserved stored values, don't clobber the cache with empties.
      const attMirrorBlanked =
        editing.attainment.every((a) => (a || "").trim() === "") &&
        (od.qualifiedValue || []).some(
          (q: { context?: { description?: string } }) => (q.context?.description || "").trim() !== "",
        );
      if (od.qualifiedValue && !attMirrorBlanked) {
        for (let i = 0; i < Math.min(5, od.qualifiedValue.length); i++) {
          if (!od.qualifiedValue[i].context) {
            od.qualifiedValue[i].context = { text: "" };
          }
          od.qualifiedValue[i].context.description = editing.attainment[i];
        }
      }
      // Mirror the category move into the local cache so the tree re-renders
      // under the new bucket on the next render. Same non-clobbering rebuild as
      // the Firestore write (OMON-166) — preserves the v3 capability codings
      // instead of the old positional [1]/[2] overwrite.
      od.category = setCapabilityCodings(
        buildCategoryArray({
          prior: od.category || [],
          categoryCode: editing.categoryCode,
          subcategoryCode: editing.subcategoryCode,
          categoryDisplay: newCatNode.display,
          subcategoryDisplay: newAimNode?.display || "",
        }),
        editing.capabilities,
      ) as typeof od.category;
    }
    if (adSnap.exists()) {
      const ad = state.activityData.get(name);
      if (ad) {
        ad.title = editing.title;
        // OMON-242: mirror the SAME blank-prompt guard as the Firestore write. This
        // local activityData cache write is what getObjectiveDetail seeds from — and
        // it was NOT covered by the Firestore-side guard. An empty write here poisons
        // the cache, so every later seed comes up blank and the fact renders wiped
        // even while Firestore is intact (the self-sustaining degradation). Refuse to
        // overwrite cached prompts with all-empty content.
        const incomingPromptsAllEmpty = editing.prompts.every((pr) => stripGuidanceHeader(pr || "").trim() === "");
        const cacheHasPrompts = (ad.documentation || []).some(
          (d: { display?: string }) => stripGuidanceHeader(d.display || "").trim() !== "",
        );
        if (!(incomingPromptsAllEmpty && cacheHasPrompts)) {
          ad.documentation = editing.prompts.map((text, i) => ({
            type: "documentation",
            label: LEVEL_NAMES[i],
            display: text,
          }));
        }
        if (guidanceTextChanged(editing.guidanceText, ad as unknown as Record<string, unknown>)) {
          ad._carerGuidanceText = editing.guidanceText;
        }
      }
    }

    // OMON-166 Part 2.5: auto-save (silent) persists the field but keeps the
    // always-edit inspector open — don't exit edit mode, toast, or re-render
    // (a re-render would steal focus mid-typing). The classic drill-in Save path
    // (non-silent) still confirms + exits as before.
    if (!opts.silent) {
      state.catalogueEditMode = false;
      state.catalogueEditPreview = false;
      state.editingDetail = null;
      const savedTitle = state.catalogueData.get(name)?.title || name;
      statusEl.textContent = `Saved changes to "${savedTitle}"`;
      showToast(`Saved changes to "${savedTitle}"`, "success");
      renderCatalogue();
    }

    // OMON-182: undo toast on silent (auto-save) writes that actually changed
    // something. Restoring re-seeds the inspector from the snapshot and writes
    // it back through this same function (suppressUndo stops recursion).
    if (opts.silent && !opts.suppressUndo && undoSnapshot && editing) {
      const diff = diffEditingDetail(editing, undoSnapshot);
      if (diff.hasChanges) {
        const savedTitle = editing.title || name;
        const fields = diff.changedFields.slice(0, 3).join(", ")
          + (diff.changedFields.length > 3 ? "…" : "");
        const restore = { ...undoSnapshot, attainment: [...undoSnapshot.attainment], prompts: [...undoSnapshot.prompts], capabilities: [...undoSnapshot.capabilities] };
        showUndoToast(`Saved "${savedTitle}" — ${fields}`, () => {
          editSessionEpoch++;
          state.catalogueSelectedGoal = name;
          state.editingDetail = restore;
          editingForName = name;
          state.catalogueEditMode = true;
          renderCatalogue();
          void saveEditingObjective(name, { silent: true, suppressUndo: true });
        });
      }
    }
  } catch (err) {
    console.error("Failed to save objective:", err);
    statusEl.textContent = "Error saving -- check console";
    if (!opts.silent) showToast("Error saving — check console", "error");
  }
}

/**
 * OMON-166 Part 4: rename a fact in place (double-click title / context-menu
 * Rename). Writes the title on both the OD and AD and the denormalised
 * `code.coding[0].display`, then mirrors into the local cache. Goals that
 * reference this fact resolve their title live via stable code (OMON-004
 * Decision 1), so no sweep is needed. Title-only — no category/capability churn.
 */
export async function renameFact(name: string, newTitle: string): Promise<boolean> {
  const title = newTitle.trim();
  if (!title) return false;
  const od = state.catalogueData.get(name);
  try {
    const odRef = doc(db, "_observationDefinition", name);
    const odSnap = await getDoc(odRef);
    if (odSnap.exists()) {
      const data = odSnap.data();
      const codeCoding = data.code?.coding || [];
      if (codeCoding[0]) codeCoding[0].display = title;
      await updateDoc(odRef, { title, "code.coding": codeCoding });
    }
    const adRef = doc(db, "_activityDefinition", name);
    const adSnap = await getDoc(adRef);
    if (adSnap.exists()) {
      await updateDoc(adRef, { title });
    }
    // Local-cache mirror so the tree + inspector show the new title immediately.
    if (od) od.title = title;
    const ad = state.activityData.get(name);
    if (ad) ad.title = title;
    statusEl.textContent = `Renamed to "${title}"`;
    return true;
  } catch (err) {
    console.error("Failed to rename fact:", name, err);
    statusEl.textContent = "Error renaming -- check console";
    return false;
  }
}

/**
 * OMON-166 Part 3: recategorise a single fact (drag-to-recategorise). Writes
 * the goal-category coding — and the sub-category, or clears it when moving to a
 * plain category — on BOTH the OD and AD, PRESERVING the trackable-capability
 * codings (system-based rebuild via buildCategoryArray + setCapabilityCodings,
 * never positional). Scoped to category only: no title/SMART/attainment churn,
 * unlike saveEditingObjective. Mirrors into the local cache so the tree repaints
 * under the new bucket. Variant alignment is the caller's job (tree.ts onEnd
 * loops the parent's variantChildIds through this).
 */
export async function recategoriseFact(
  name: string,
  newCatCode: string,
  newSubCode: string,
): Promise<boolean> {
  const od = state.catalogueData.get(name);
  if (!od) return false;
  const catNode = state.goalCategories.get(newCatCode);
  if (!catNode) return false; // unknown destination category — refuse the move
  const subNode = newSubCode
    ? state.goalSubcategories.get(`${newCatCode}__${newSubCode}`)
    : undefined;
  const caps = trackableCapabilities(od);

  const rebuild = (prior: unknown[]) =>
    setCapabilityCodings(
      buildCategoryArray({
        prior,
        categoryCode: newCatCode,
        subcategoryCode: newSubCode,
        categoryDisplay: catNode.display,
        subcategoryDisplay: subNode?.display || "",
      }),
      caps,
    );

  try {
    const odRef = doc(db, "_observationDefinition", name);
    const odSnap = await getDoc(odRef);
    if (odSnap.exists()) {
      await updateDoc(odRef, { category: rebuild(odSnap.data().category || []) });
    }
    const adRef = doc(db, "_activityDefinition", name);
    const adSnap = await getDoc(adRef);
    if (adSnap.exists()) {
      await updateDoc(adRef, { category: rebuild(adSnap.data().category || []) });
    }
    // Local-cache mirror (feedback_silent_success_stale_cache): the tree reads
    // category from these Maps to bucket the fact, so mirror every write.
    od.category = rebuild(od.category || []) as typeof od.category;
    const ad = state.activityData.get(name);
    if (ad && Array.isArray((ad as { category?: unknown[] }).category)) {
      (ad as { category: unknown[] }).category = rebuild(
        (ad as { category: unknown[] }).category,
      );
    }
    return true;
  } catch (err) {
    console.error("Failed to recategorise fact:", name, err);
    statusEl.textContent = "Error moving fact -- check console";
    return false;
  }
}

/**
 * OMON-188: re-wire an EXISTING standalone fact to become a variant of a parent
 * fact (drag-into-group). Aligns the child's category to the parent's (variants
 * follow their parent), points the child at the parent (`_variantOf`), and adds
 * the forward edge to the parent's `hasMember` (+ flags it `_trackableParent`).
 * Mirrors the cache so the tree repaints nested without waiting for onSnapshot.
 * Refuses if the child is itself a parent (has variants) — no nesting parents.
 */
export async function makeVariantOf(childName: string, parentName: string): Promise<boolean> {
  if (childName === parentName) return false;
  const child = state.catalogueData.get(childName);
  const parent = state.catalogueData.get(parentName);
  if (!child || !parent) return false;
  if (Array.isArray(child.hasMember) && child.hasMember.length > 0) return false; // don't nest a parent
  if (child._variantOf) return false; // already a variant
  const pc = resolveCategoryCodes(parent.category);
  try {
    // Align the child's category to the parent's (reuses the OD+AD+cache+
    // capability rebuild) so it buckets/nests correctly.
    if (pc.goalCategory) {
      const ok = await recategoriseFact(childName, pc.goalCategory, pc.goalSubcategory || "");
      if (!ok) return false;
    }
    await updateDoc(doc(db, "_observationDefinition", childName), {
      _variantOf: { reference: `_observationDefinition/${parentName}` },
    });
    const existing = Array.isArray(parent.hasMember) ? parent.hasMember : [];
    const newMembers = [...existing, { reference: `_observationDefinition/${childName}` }];
    await updateDoc(doc(db, "_observationDefinition", parentName), {
      hasMember: newMembers,
      _trackableParent: true,
    });
    // Cache mirror (feedback_silent_success_stale_cache).
    child._variantOf = { reference: `_observationDefinition/${parentName}` };
    parent.hasMember = newMembers;
    parent._trackableParent = true;
    return true;
  } catch (err) {
    console.error("Failed to make variant:", childName, "->", parentName, err);
    statusEl.textContent = "Error nesting fact -- check console";
    return false;
  }
}

/**
 * OMON-188: inverse of makeVariantOf (the drag's Undo). Detaches the child from
 * its parent (clears `_variantOf`, drops the parent's `hasMember` edge) and moves
 * it back out to the given category / sub-category as a standalone fact.
 */
export async function unmakeVariant(
  childName: string,
  parentName: string,
  restoreCat: string,
  restoreSub: string,
): Promise<boolean> {
  const child = state.catalogueData.get(childName);
  const parent = state.catalogueData.get(parentName);
  if (!child || !parent) return false;
  const childRefStr = `_observationDefinition/${childName}`;
  try {
    await updateDoc(doc(db, "_observationDefinition", childName), { _variantOf: deleteField() });
    const remaining = (Array.isArray(parent.hasMember) ? parent.hasMember : [])
      .filter((m) => m.reference !== childRefStr);
    const parentUpdate: Record<string, unknown> = { hasMember: remaining };
    // OMON-190: an empty variant group is no longer a trackable parent — clear the
    // flag so the ex-parent renders (and behaves) as a normal standalone fact, not a
    // degenerate "0 variants" group. Also fixes the OMON-188 drag-in Undo, which
    // previously left a fact that was never a parent stuck as an empty parent.
    if (remaining.length === 0) parentUpdate._trackableParent = deleteField();
    await updateDoc(doc(db, "_observationDefinition", parentName), parentUpdate);
    delete child._variantOf;
    parent.hasMember = remaining;
    if (remaining.length === 0) delete parent._trackableParent;
    if (restoreCat) await recategoriseFact(childName, restoreCat, restoreSub);
    return true;
  } catch (err) {
    console.error("Failed to un-variant:", childName, err);
    statusEl.textContent = "Error undoing nest -- check console";
    return false;
  }
}
